import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import { ApiError } from '@/shared/api/http'
import { MIN_PASSWORD } from '@/stores/SettingsStore'

export type ResetStage = 'email' | 'code' | 'password' | 'done'

/**
 * Восстановление пароля: почта → код с почты → новый пароль.
 *
 * Отдельный стор, а не состояние компонента: шагов три, между ними
 * живёт разовый токен, и потерять его при перерисовке нельзя — иначе
 * человек, дошедший до последнего шага, начинает всё заново.
 *
 * Создаётся на экране и умирает вместе с ним: хранить в корневом
 * сторе незачем, а держать чужой токен смены пароля дольше нужного
 * — лишний риск.
 */
export class PasswordResetStore {
  stage: ResetStage = 'email'
  email = ''
  busy = false
  error: string | null = null

  private resetToken: string | null = null

  constructor() {
    makeAutoObservable<this, 'resetToken'>(this, { resetToken: false })
  }

  /** Затёртая почта для подписи «код отправлен на h***i@example.com». */
  get maskedEmail(): string {
    const [name = '', domain = ''] = this.email.split('@')
    if (!domain) return this.email
    const head = name.slice(0, 1)
    const tail = name.slice(-1)
    return `${head}***${name.length > 1 ? tail : ''}@${domain}`
  }

  async requestCode(email: string): Promise<void> {
    this.busy = true
    this.error = null
    try {
      await api.auth.requestReset(email.trim())
      runInAction(() => {
        this.email = email.trim()
        this.stage = 'code'
      })
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async confirmCode(code: string): Promise<void> {
    this.busy = true
    this.error = null
    try {
      const result = await api.auth.confirmReset(this.email, code.trim())
      runInAction(() => {
        this.resetToken = result.reset_token
        this.stage = 'password'
      })
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  async setPassword(password: string, confirmation: string): Promise<void> {
    if (password !== confirmation) {
      this.error = 'Пароли не совпадают'
      return
    }
    if (this.resetToken === null) {
      this.error = 'Срок ссылки истёк'
      this.stage = 'email'
      return
    }
    this.busy = true
    this.error = null
    try {
      await api.auth.completeReset(this.resetToken, password)
      runInAction(() => {
        this.resetToken = null
        this.stage = 'done'
      })
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  back(): void {
    this.error = null
    if (this.stage === 'code') this.stage = 'email'
    else if (this.stage === 'password') this.stage = 'code'
  }
}

function explain(cause: unknown): string {
  if (cause instanceof ApiError) {
    switch (cause.code) {
      case 'invalid_code':
        return 'Неверный код'
      case 'code_expired':
      case 'invalid_reset_token':
      case 'token_expired':
        return 'Срок кода истёк'
      case 'weak_password':
        // Бекенд проверяет ТОЛЬКО длину (`auth.py`: len < 8).
        // Обещать требования к составу, которых сервер не проверяет,
        // значит заставить человека придумывать сложнее без причины.
        return `Пароль короче ${MIN_PASSWORD} символов`
      case 'user_not_found':
        // Специально то же сообщение, что и при успехе: иначе форма
        // превращается в способ проверять, есть ли такая почта в системе.
        return 'Если почта зарегистрирована, код отправлен'
      case 'too_many_requests': {
        const wait = cause.retryAfter
        return wait ? `Слишком часто, подождите ${wait} с` : 'Слишком часто, подождите'
      }
      default:
        return cause.message || 'Не удалось выполнить'
    }
  }
  return 'Сервер не отвечает'
}
